import AuthenticationLayout from 'components/AuthLayout';
import Button from 'components/Button';
import Error from 'components/form/Error';
import CountryInput from 'components/form/Inputs/CountryInput';
import Input from 'components/form/Inputs/Input';
import PasswordInput from 'components/form/Inputs/PasswordInput';
import Radio from 'components/form/Inputs/Radio';
import ProfessionalSituationField from 'components/form/ProfessionalSituationField';
import PasswordRequirements from 'components/form/PasswordRequirements';
import Head from 'components/profileNavigation/Head';
import { GenderValues } from 'enums/Genders';
import {
  ProfessionalSituationValues,
  professionalSituationRequiresDetails,
} from 'enums/ProfessionalSituation';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { mutate } from 'swr';
import isPasswordValid from 'utils/isPasswordValid';
import publicAPI from 'utils/publicAxiosInstance';

const Subtitle = styled.div`
  color: var(--gray-500);
  margin-top: 2.25rem;
  font-size: 1rem;
  text-align: justify;
  p {
    margin-bottom: 1rem;
  }
  width: 60rem;
  max-width: 100%;
`;

const FormContainer = styled.div`
  width: 60rem;
  max-width: 100%;
  margin-top: 1.25rem;
`;

const Line = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--gray-200);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;

  button {
    margin-left: auto;
    margin-right: auto;
    margin-top: 3rem;
    margin-bottom: 4rem;
    padding: 0.7rem 1.1rem;
  }
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const FormSection = styled.div`
  p {
    margin-top: 1.5rem;
  }
  width: 100%;

  @media (min-width: 900px) {
    &:first-child {
      margin-right: 3rem;
    }
  }
`;
const Label = styled.p`
  font-size: 0.9rem;
  margin-bottom: 0.375rem;
  color: var(--gray-700);
`;

const RadiosContainer = styled.div`
  padding: 0.8rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 0.8rem;
`;

const GeneralErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.8rem;
`;

const EmailNotice = styled.p`
  color: var(--red-500, #dc2626);
  font-weight: 700;
  font-size: 0.95rem;
  text-align: center;
  margin-top: 2rem;
  margin-bottom: -1.5rem;
`;

type FormState = {
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  gender: GenderValues;
  address: string;
  region: string;
  postalCode: string;
  city: string;
  country: string;
  /** Complément en texte libre de la situation actuelle. */
  organism: string;
  professionalSituation: ProfessionalSituationValues | '';
  dateOfBirth: string;
};

const initialState: FormState = {
  lastName: '',
  firstName: '',
  email: '',
  phone: '',
  password: '',
  passwordConfirm: '',
  gender: 'MALE',
  address: '',
  region: '',
  postalCode: '',
  city: '',
  country: '',
  organism: '',
  professionalSituation: '',
  dateOfBirth: '',
};

type Errors = {
  general: boolean;
  passwordMatch: boolean;
  passwordStrength: boolean;
  email: boolean;
  phoneUsed: boolean;
  phone: boolean;
  adult: boolean;
  professionalSituation: boolean;
  organism: boolean;
};

const initialErrors: Errors = {
  general: false,
  passwordMatch: false,
  passwordStrength: false,
  email: false,
  phoneUsed: false,
  phone: false,
  adult: false,
  professionalSituation: false,
  organism: false,
};

const Signup: FC = () => {
  const [state, setState] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement & HTMLSelectElement
  > = (e) => {
    setErrors(initialErrors);
    if (e.target.name === 'gender') {
      return setState({
        ...state,
        gender:
          e.target.value === 'MALE_gender'
            ? 'MALE'
            : e.target.value === 'FEMALE_gender'
            ? 'FEMALE'
            : 'NOT_SPECIFIED',
      });
    }

    if (e.target.name === 'phone') {
      let value = e.target.value.replace(/\D/g, '');
      value = value.substring(0, 10);
      const formattedValue = value.replace(/(\d{2})(?=\d)/g, '$1 ');
      return setState({
        ...state,
        phone: formattedValue,
      });
    }

    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    if (state.password !== state.passwordConfirm) {
      setLoading(false);
      setErrors({
        ...initialErrors,
        passwordMatch: true,
      });
      return;
    }

    if (!isPasswordValid(state.password)) {
      setLoading(false);
      return setErrors({
        ...initialErrors,
        passwordStrength: true,
      });
    }

    if (!state.professionalSituation) {
      setLoading(false);
      window.scrollTo({ top: document.body.scrollHeight });
      return setErrors({
        ...initialErrors,
        professionalSituation: true,
      });
    }

    if (
      professionalSituationRequiresDetails(state.professionalSituation) &&
      !state.organism.trim()
    ) {
      setLoading(false);
      window.scrollTo({ top: document.body.scrollHeight });
      return setErrors({
        ...initialErrors,
        organism: true,
      });
    }

    let dateReference = new Date();
    const dateReferenceArray = dateReference.toISOString().split('-');
    dateReferenceArray[0] = (parseInt(dateReferenceArray[0]) - 18).toString();
    dateReference = new Date(dateReferenceArray.join('-'));

    if (dateReference.getTime() - new Date(state.dateOfBirth).getTime() < 0) {
      setLoading(false);
      return setErrors({
        ...initialErrors,
        adult: true,
      });
    }

    const newDateOfBirth = new Date(state.dateOfBirth);
    publicAPI
      .post('/user/signup/mentor', {
        ...state,
        dateOfBirth:
          newDateOfBirth.toString() === 'Invalid Date' ? null : newDateOfBirth,
        organism: professionalSituationRequiresDetails(
          state.professionalSituation,
        )
          ? state.organism.trim()
          : null,
      })
      .then((res) => {
        setLoading(false);
        const {
          data: { accessToken },
        } = res;
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('accessToken', accessToken);
          mutate('/auth/profile', undefined);
          navigate(
            '/authentication/verification-email-sent?next=/mentor/welcome',
          );
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err && err.response && err.response.status === 400) {
          if (err.response.data && err.response.data.fields) {
            if (err.response.data.fields.includes('email')) {
              return setErrors({
                ...initialErrors,
                email: true,
              });
            }
            if (err.response.data.fields.includes('phone')) {
              return setErrors({
                ...initialErrors,
                phoneUsed: true,
              });
            }
          }
          if (err.response.data?.message[0]?.includes('phone')) {
            return setErrors({
              ...initialErrors,
              phone: true,
            });
          }
        }
        return setErrors({
          ...initialErrors,
          general: true,
        });
      });
  };

  return (
    <AuthenticationLayout title="Devenir mentor de jeunes engagés" backTo="/" src_image="/logo_institut_mentorat.png">
      <Subtitle>
        <p>
          L'Institut de l'Engagement vous propose de consacrer du temps (au
          moins une rencontre, en présence ou à distance, toutes les 6 semaines,
          pendant 6 mois) pour mentorer un jeune, l'écouter, le conseiller,
          faire écho à ses questions, l'aider dans ses candidatures, lui donner
          un appui pour franchir une étape.
        </p>

        <p>
          Les jeunes auxquels l'Institut propose un mentorat se sont tous
          engagés, lors d'un volontariat, lors d'un Service Civique ou d'un
          bénévolat consistant. Leurs situations et leurs projets sont très
          divers !
        </p>
        <p>
          Durant tout le mentorat, l'Institut sera à vos côtés pour vous
          informer ou vous former sur le rôle du mentor. Notre équipe pourra
          vous donner des outils, répondre à vos questions ou alertes.
        </p>
        <p>
          L'objet de ce questionnaire est de mieux vous connaître pour proposer
          les mentorats adaptés à la situation du mentor et du mentoré.
        </p>
      </Subtitle>

      <FormContainer>
        <Head
          title="Informations générales"
          subtitle="Liste toutes les informations sur l'utilisateur"
        />
        <Line />
        <Form onSubmit={handleSubmit}>
          {errors.general && (
            <GeneralErrorContainer>
              <Error>Une erreur s'est produite.</Error>
            </GeneralErrorContainer>
          )}
          <FormContent>
            <FormSection>
              <Input
                label="Nom *"
                placeholder="Nom"
                required
                value={state.lastName}
                onChange={handleChange}
                name="lastName"
              />
              <Input
                label="Prénom *"
                placeholder="Prénom"
                required
                value={state.firstName}
                onChange={handleChange}
                name="firstName"
              />
              {errors.email && (
                <Error>
                  Cette adresse email est invalide ou déjà utilisée.
                </Error>
              )}
              <Input
                label="Adresse email *"
                placeholder="exemple@mail.com"
                type="email"
                required
                value={state.email}
                onChange={handleChange}
                name="email"
              />
              {errors.phoneUsed && (
                <Error>Ce numéro de téléphone est déjà utilisé.</Error>
              )}
              {errors.phone && <Error>Ce numéro de téléphone invalide.</Error>}
              <Input
                label="Numéro de téléphone *"
                placeholder="01 23 45 67 89"
                required
                value={state.phone}
                onChange={handleChange}
                name="phone"
              />
              {errors.adult && (
                <Error>
                  Vous devez être majeur pour vous inscrire en tant que mentor.
                </Error>
              )}
              <Input
                label="Date de naissance *"
                type="date"
                placeholder="aaaa-mm-jj (ex: 2022-05-19)"
                value={state.dateOfBirth}
                onChange={handleChange}
                name="dateOfBirth"
                required
              />

              {errors.passwordMatch && (
                <Error>Les mots de passe ne correspondent pas.</Error>
              )}
              {errors.passwordStrength && (
                <Error>Le mot de passe n'est pas assez fort.</Error>
              )}
              <PasswordInput
                label="Mot de passe *"
                placeholder="Mot de passe"
                required
                value={state.password}
                onChange={handleChange}
                name="password"
              />
              <PasswordInput
                label="Confirmer votre mot de passe *"
                placeholder="Mot de passe"
                required
                value={state.passwordConfirm}
                onChange={handleChange}
                name="passwordConfirm"
              />
              <PasswordRequirements password={state.password} marginTop={1} />
            </FormSection>
            <FormSection>
              <Label>Vous êtes</Label>
              <RadiosContainer>
                <Radio
                  label="Un homme"
                  name="gender"
                  onChange={handleChange}
                  checked={state.gender === 'MALE'}
                  id="MALE"
                />
                <Radio
                  label="Une femme"
                  name="gender"
                  onChange={handleChange}
                  checked={state.gender === 'FEMALE'}
                  id="FEMALE"
                />
                <Radio
                  label="Autre"
                  name="gender"
                  onChange={handleChange}
                  checked={state.gender === 'NOT_SPECIFIED'}
                  id="NOT_SPECIFIED"
                />
              </RadiosContainer>
              <Input
                label="Adresse"
                placeholder="Numéro et adresse"
                value={state.address}
                onChange={handleChange}
                name="address"
              />
              <div style={{ marginBottom: '1.5rem' }}>
                <Label>Région *</Label>
                <select
                  name="region"
                  required
                  value={state.region}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    height: '42px',
                    padding: '0 0.8rem',
                    borderRadius: '5px',
                    border: '1px solid var(--gray-300)',
                    backgroundColor: 'white',
                    fontSize: '0.9rem',
                    color: state.region ? 'var(--gray-900)' : 'var(--gray-500)',
                    outline: 'none',
                    appearance: 'auto'
                  }}
                >
                  <option value="" disabled>Sélectionnez votre région</option>
                  <option value="Auvergne-Rhône-Alpes">Auvergne-Rhône-Alpes</option>
                  <option value="Bourgogne-Franche-Comté">Bourgogne-Franche-Comté</option>
                  <option value="Bretagne">Bretagne</option>
                  <option value="Centre-Val de Loire">Centre-Val de Loire</option>
                  <option value="Corse">Corse</option>
                  <option value="Grand Est">Grand Est</option>
                  <option value="Hauts-de-France">Hauts-de-France</option>
                  <option value="Île-de-France">Île-de-France</option>
                  <option value="Normandie">Normandie</option>
                  <option value="Nouvelle-Aquitaine">Nouvelle-Aquitaine</option>
                  <option value="Occitanie">Occitanie</option>
                  <option value="Pays de la Loire">Pays de la Loire</option>
                  <option value="Provence-Alpes-Côte d'Azur">Provence-Alpes-Côte d'Azur</option>
                  <option value="Guadeloupe">Guadeloupe</option>
                  <option value="Guyane">Guyane</option>
                  <option value="La Réunion">La Réunion</option>
                  <option value="Martinique">Martinique</option>
                  <option value="Mayotte">Mayotte</option>
                  <option value="Nouvelle-Calédonie">Nouvelle-Calédonie</option>
                  <option value="Étranger">Étranger</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <Input
                label="Code postal *"
                placeholder="Code postal"
                required
                value={state.postalCode}
                onChange={handleChange}
                name="postalCode"
              />
              <Input
                label="Ville"
                placeholder="Ville"
                value={state.city}
                onChange={handleChange}
                name="city"
              />
              <CountryInput
                value={state.country}
                setValue={(val) => setState({ ...state, country: val })}
                label="Pays"
              />
              <ProfessionalSituationField
                value={state.professionalSituation}
                details={state.organism}
                onChange={(professionalSituation, organism) => {
                  setErrors(initialErrors);
                  setState({ ...state, professionalSituation, organism });
                }}
                situationError={errors.professionalSituation}
                detailsError={errors.organism}
              />
            </FormSection>
          </FormContent>

                <EmailNotice>
              ⚠️ En cliquant sur "S'inscrire", vous recevrez un email pour valider votre adresse. Vous pourrez ensuite remplir le questionnaire et signer la charte pour finaliser votre inscription au programme de mentorat.
              </EmailNotice>
              <Button color="primary" loading={loading}>
            S'inscrire
          </Button>
        </Form>
      </FormContainer>
    </AuthenticationLayout>
  );
};

export default Signup;
